import React, { useEffect, useState } from 'react';
import { useParams, Navigate, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Stack,
  Grid,
  Card,
  CardContent,
  Button,
  Drawer,
  Snackbar,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { RequestQuote, ExpandMore, LocationOn, Schedule } from '@mui/icons-material';
import PageLayout from '../components/PageLayout';
import { getBookingFlowPath } from '../utils/featureFlags';
import Seo from '../components/Seo';
import ServicesGrid from '../components/ServicesGrid';
import CustomerReviews from '../components/CustomerReviews';
import FAQ from '../components/FAQ';
import QuoteQuestionnaireAdapter from '../components/questionnaire/QuoteQuestionnaireAdapter';
import { QuoteProvider } from '../contexts/QuoteContext';
import { QuestionnaireProvider } from '../contexts/QuestionnaireContext';
import { trackButtonClick } from '../utils/analytics';
import serviceAreas from '../data/serviceAreas.json';
import { buildCityJsonLd } from '../utils/cityJsonLd';

const neighborhoodName = (n) => (typeof n === 'string' ? n : n.name);
const neighborhoodBlurb = (n) => (typeof n === 'string' ? null : n.blurb);

const CityLandingPage = () => {
  const { citySlug } = useParams();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [citySlug]);

  const city = serviceAreas.cities.find((c) => c.slug === citySlug);

  // Unknown slug → send crawlers/users to the hub rather than a soft-200 dead end.
  if (!city) {
    return <Navigate to="/service-areas" replace />;
  }

  const openQuote = (location) => {
    trackButtonClick('get_quote_questionnaire', location);
    setDrawerOpen(true);
  };

  const handleBookNow = (location) => {
    trackButtonClick('book_now', location);
    navigate(getBookingFlowPath());
  };

  const handleComplete = () => {
    setDrawerOpen(false);
    setContactSuccess(true);
  };

  return (
    <PageLayout>
      <Seo
        title={city.metaTitle}
        description={city.metaDescription}
        path={`/house-cleaning/${city.slug}`}
        jsonLd={buildCityJsonLd(city)}
      />

      {/* Hero — the single <h1>, intro copy, and primary CTA (all static → prerendered) */}
      <Box
        component="section"
        sx={{
          background: 'linear-gradient(135deg, #000000 0%, #FFD700 100%)',
          color: 'white',
          py: { xs: 6, md: 10 },
        }}
      >
        <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
          <Typography
            component="h1"
            variant="h3"
            sx={{ fontWeight: 800, mb: 1.5, fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' } }}
          >
            {city.h1}
          </Typography>
          <Typography variant="subtitle1" sx={{ mb: 3, opacity: 0.9 }}>
            {city.county} &bull; {city.region}
          </Typography>
          <Typography
            variant="body1"
            sx={{ maxWidth: 820, mx: 'auto', mb: 4, fontSize: { xs: '1rem', md: '1.125rem' }, lineHeight: 1.7 }}
          >
            {city.intro}
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="center"
            alignItems="center"
          >
            <Button
              variant="contained"
              size="large"
              startIcon={<Schedule />}
              onClick={() => handleBookNow(`city_hero_${city.slug}`)}
              sx={{
                bgcolor: '#4CAF50',
                color: 'white',
                px: 4,
                py: 1.5,
                fontWeight: 800,
                borderRadius: 3,
                textTransform: 'none',
                '&:hover': { bgcolor: '#45a049', transform: 'translateY(-2px)' },
                transition: 'all 0.3s ease',
              }}
            >
              Book Now
            </Button>
            <Button
              variant="contained"
              size="large"
              startIcon={<RequestQuote />}
              onClick={() => openQuote(`city_hero_${city.slug}`)}
              sx={{
                bgcolor: 'white',
                color: 'text.primary',
                px: 4,
                py: 1.5,
                fontWeight: 700,
                borderRadius: 3,
                textTransform: 'none',
                '&:hover': { bgcolor: 'grey.100', transform: 'translateY(-2px)' },
                transition: 'all 0.3s ease',
              }}
            >
              Get a Free Quote
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* Neighborhoods We Serve — the core unique, SEO-relevant content block */}
      {city.neighborhoods && city.neighborhoods.length > 0 && (
        <Container maxWidth="lg" sx={{ py: { xs: 5, md: 8 } }}>
          <Typography
            component="h2"
            variant="h4"
            align="center"
            sx={{ fontWeight: 700, mb: 1, color: 'primary.main' }}
          >
            Neighborhoods We Clean in {city.city}
          </Typography>
          <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4, maxWidth: 720, mx: 'auto' }}>
            Trusted, insured house cleaning across {city.city} and the surrounding {city.county} area.
          </Typography>
          <Grid container spacing={3}>
            {city.neighborhoods.map((n) => {
              const name = neighborhoodName(n);
              const blurb = neighborhoodBlurb(n);
              return (
                <Grid item xs={12} sm={6} md={4} key={name}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography
                        variant="h6"
                        sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600, mb: blurb ? 1 : 0 }}
                      >
                        <LocationOn fontSize="small" color="primary" />
                        {name}
                      </Typography>
                      {blurb && (
                        <Typography variant="body2" color="text.secondary">
                          {blurb}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      )}

      {/* Services offered (async catalog — not SEO-critical) */}
      <ServicesGrid showTitle />

      {/* Social proof */}
      <CustomerReviews />

      {/* FAQ — city-specific when provided, else the standard set */}
      {city.faq && city.faq.length > 0 ? (
        <Container maxWidth="md" sx={{ py: { xs: 5, md: 8 } }}>
          <Typography component="h2" variant="h4" align="center" sx={{ fontWeight: 700, mb: 4, color: 'primary.main' }}>
            {city.city} House Cleaning FAQ
          </Typography>
          {city.faq.map((item, idx) => (
            <Accordion key={idx} disableGutters>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography sx={{ fontWeight: 600 }}>{item.question}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography color="text.secondary">{item.answer}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Container>
      ) : (
        <FAQ />
      )}

      {/* Closing CTA */}
      <Box component="section" sx={{ bgcolor: 'grey.900', color: 'white', py: { xs: 6, md: 8 } }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography component="h2" variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
            Ready for a spotless home in {city.city}?
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
            Get a free, no-obligation quote from 5 Fairies Cleaning today.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" alignItems="center">
            <Button
              variant="contained"
              size="large"
              startIcon={<Schedule />}
              onClick={() => handleBookNow(`city_cta_${city.slug}`)}
              sx={{
                bgcolor: '#4CAF50',
                color: 'white',
                px: 4,
                py: 1.5,
                fontWeight: 800,
                borderRadius: 3,
                textTransform: 'none',
                '&:hover': { bgcolor: '#45a049' },
              }}
            >
              Book Now
            </Button>
            <Button
              variant="contained"
              size="large"
              startIcon={<RequestQuote />}
              onClick={() => openQuote(`city_cta_${city.slug}`)}
              sx={{ px: 4, py: 1.5, fontWeight: 700, borderRadius: 3, textTransform: 'none' }}
            >
              Get a Free Quote
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* Quote questionnaire drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: '90%', md: '70%', lg: '60%' },
            maxWidth: 800,
            bgcolor: 'background.paper',
          },
        }}
      >
        <QuoteProvider>
          <QuestionnaireProvider>
            <QuoteQuestionnaireAdapter
              onComplete={handleComplete}
              onClose={() => setDrawerOpen(false)}
              configPath="/data/customer_engagement_questionnaire.json"
              serviceContext={{ serviceType: 'House Cleaning', city: city.city }}
            />
          </QuestionnaireProvider>
        </QuoteProvider>
      </Drawer>

      <Snackbar
        open={contactSuccess}
        autoHideDuration={5000}
        onClose={() => setContactSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setContactSuccess(false)} severity="success" sx={{ width: '100%' }}>
          Quote request sent! We'll be in touch soon with your personalized estimate.
        </Alert>
      </Snackbar>
    </PageLayout>
  );
};

// Provider-wrapped export (mirrors ServiceDetail) so the quote flow has context.
const CityLandingPageWithProviders = () => (
  <QuoteProvider>
    <QuestionnaireProvider>
      <CityLandingPage />
    </QuestionnaireProvider>
  </QuoteProvider>
);

export default CityLandingPageWithProviders;
